/**
 * One record's files, and the uploads behind them.
 *
 * Kept out of `EntityFiles.tsx` for the same reason `useComposerAttachments` is
 * kept out of the mail composer's tray: this is a state machine, not layout. A
 * picked file is refused, in flight or stored; the list underneath it is
 * loading, loaded or failed; and the caps that decide the first of those arrive
 * from a second request that may still be running when somebody drops a file on
 * the card. Mixing that into the component that draws the card is how a "remove"
 * button ends up deleting the row above the one that was clicked.
 *
 * ## What it deliberately does NOT do
 *
 * A failed upload leaves no row. The composer's tray keeps one, because there
 * the list is a staging area whose rows a send will name; here the list IS the
 * record of what is stored, and a row for a file that never landed would be the
 * one lie a file list cannot afford. The reason is reported instead, and it
 * stays on screen until the next pick rather than passing through a toast - a
 * refusal here is a sentence long enough that 2.8 seconds is not time to read
 * it.
 *
 * The count ceiling is not enforced here either. The card disables its picker
 * once the list is full, which says the same thing where the operator is about
 * to act; a race past it answers 409 and lands in the generic sentence.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteCrmAttachment,
  getCrmAttachmentLimits,
  listCrmAttachments,
  uploadCrmAttachment,
  type CrmAttachment,
  type CrmAttachmentEntity,
  type CrmAttachmentLimits,
} from "../../../lib/crmAttachments";

/** A file on its way up, before it is a row anybody can download. */
export interface PendingFile {
  /** Stable for the row's whole life - there is no server id to key on yet. */
  localId: string;
  name: string;
  size: number;
}

export interface EntityFilesState {
  /** Null while the first read is in flight. See below for why that is not `[]`. */
  rows: CrmAttachment[] | null;
  /** Null until the server has answered. See below for why that is not a guess. */
  limits: CrmAttachmentLimits | null;
  pending: PendingFile[];
  /** i18n keys for the current pick's refusals, deduplicated and translated by
   *  the caller - a hook has no business holding a `t`. */
  refusals: string[];
  /** Stored rows plus the ones on their way: what the ceiling is measured against. */
  count: number;
  /** True once the owner is carrying as many files as the server will keep. */
  full: boolean;
  /** True while anything is going up. */
  uploading: boolean;
  add: (picked: FileList | File[] | null) => void;
  /** Awaited by the caller, so a confirm dialog can stay busy until it is done. */
  remove: (file: CrmAttachment) => Promise<void>;
}

/** What the hook tells the screen about, beyond the state it hands back. */
interface Reports {
  /**
   * A read that came back empty because it never came back at all.
   *
   * Reported rather than swallowed, because the empty card cannot say it: "no
   * files here yet" is what a failed read draws, and it is also the true answer
   * for most records - so nobody would think to doubt it.
   */
  onReadFailed?: () => void;
  /**
   * A delete that did not happen. The row stays; the caller says so.
   *
   * Carries the i18n KEY of the sentence to show, for the same reason
   * `refusals` does: the hook knows which failure this was and has no `t` to
   * turn that into words. It used to carry nothing at all, which meant the one
   * failure with a remedy - the storage refusal below - could not reach the
   * card even in principle, and every dead delete drew the same "Something went
   * wrong." beside a row that had not moved.
   */
  onWriteFailed?: (messageKey: string) => void;
  /** A counter outside this card is now stale. */
  onChange?: () => void;
}

let seq = 0;

/** The extension of a filename, lowercase and with its dot, or an empty string. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
}

/**
 * Which sentence a failed upload gets.
 *
 * Only the two refusals that are ABOUT THE FILE are named; everything else - a
 * missing owner, an empty file, a count race past the ceiling, a dead network -
 * is the generic failure. That is deliberate rather than lazy: those cases are
 * either impossible from this screen or not something an operator can act on by
 * choosing a different file, and a wrong-but-specific sentence is worse than an
 * honest general one.
 */
function refusalKey(err: unknown): string {
  const body = (err as { body?: { error?: unknown } })?.body;
  const code = typeof body?.error === "string" ? body.error : "";
  if (code === "file_too_large") return "projects.fileTooLarge";
  if (code === "blocked_type") return "projects.fileKindRefused";
  return "projects.uploadFailed";
}

/**
 * Which sentence a failed write gets.
 *
 * One refusal is named, and it is named by STATUS AND CODE TOGETHER rather than
 * by the message the transport put on the error. `502 storage_delete_failed` is
 * the delete route saying it kept the database row deliberately: the bytes are
 * still in a public-read bucket, dropping the row alone would hide the file
 * instead of deleting it and leave it readable to anyone holding the link, and
 * the row is the only thing that still names the key a retry needs. That reply
 * exists to be pressed again, so it is the one failure here worth its own
 * sentence.
 *
 * Everything else stays generic on purpose. A dead network, a 404 on a row a
 * second operator already removed, a 502 HTML page from nginx that never
 * reached Fastify and so carries no JSON body at all - none of those is fixed
 * by pressing the same button a second time, and telling an operator to retry
 * something that cannot succeed is worse than an honest general failure. Both
 * halves of the check matter for exactly that reason: the status alone would
 * turn a proxy outage into a promise, and the code alone would trust a body
 * from a reply we never asked to speak for this route.
 */
export function writeFailureKey(err: unknown): string {
  const e = err as { status?: unknown; body?: { error?: unknown } } | null | undefined;
  const code = typeof e?.body?.error === "string" ? e.body.error : "";
  if (e?.status === 502 && code === "storage_delete_failed") return "projects.removeFileStuck";
  return "common.error";
}

export function useEntityFiles({
  entity,
  entityId,
  ...reports
}: { entity: CrmAttachmentEntity; entityId: string } & Reports): EntityFilesState {
  // Null while the first read is in flight, so the spinner and the empty card
  // stay two different states.
  const [rows, setRows] = useState<CrmAttachment[] | null>(null);
  /**
   * The caps, or nothing yet.
   *
   * Deliberately not seeded with a guess. The figure the card prints beside its
   * picker is a promise about what will be accepted, and a wrong promise drawn
   * confidently is worse than no promise at all - so it appears when the server
   * has answered and not before. The local pre-check is skipped in that window;
   * the server enforces the same caps either way, so the only thing lost is the
   * early refusal.
   */
  const [limits, setLimits] = useState<CrmAttachmentLimits | null>(null);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [refusals, setRefusals] = useState<string[]>([]);

  /**
   * The three callbacks, mirrored into a ref and read from there.
   *
   * Not a nicety. Callers write them inline (`onChange={() => reloadSummary()}`),
   * so their identity changes on every render of the page around this card. A
   * `useEffect` that depended on them would refetch the list every time anything
   * on the page re-rendered, and a `useCallback` that did would rebuild `add`
   * mid-drag. The ref keeps the latest version reachable without making anything
   * depend on which render it came from.
   */
  const cb = useRef(reports);
  cb.current = reports;

  useEffect(() => {
    let alive = true;
    listCrmAttachments({ entity, entityId })
      .then((res) => { if (alive) setRows(res.rows); })
      .catch(() => {
        if (!alive) return;
        setRows([]);
        cb.current.onReadFailed?.();
      });
    return () => { alive = false; };
  }, [entity, entityId]);

  useEffect(() => {
    let alive = true;
    getCrmAttachmentLimits()
      .then((l) => { if (alive) setLimits(l); })
      // Left unknown on purpose - see `limits` above.
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  /**
   * Take a pick or a drop.
   *
   * The two local checks are the two a browser can make for free, and both exist
   * to spare the operator an upload that was never going to be kept: a 25MB file
   * refused after the fact, over a hotel connection, is a minute of somebody's
   * life. Everything else is the server's to decide.
   *
   * The rows are computed and the uploads started OUTSIDE `setPending`, never
   * from inside an updater. React's StrictMode invokes an updater twice to prove
   * it is pure, and a version of this that uploaded from within one would post
   * every picked file twice - two rows on the server, one of them orphaned from
   * the moment it was created.
   */
  const add = useCallback(
    (picked: FileList | File[] | null) => {
      const chosen = picked ? Array.from(picked) : [];
      if (chosen.length === 0) return;

      const reasons = new Set<string>();
      const accepted: File[] = [];
      for (const file of chosen) {
        const refused = !limits
          ? null
          : file.size > limits.maxBytes
            ? "projects.fileTooLarge"
            : limits.blockedExtensions.includes(extensionOf(file.name))
              ? "projects.fileKindRefused"
              : null;
        if (refused) reasons.add(refused);
        else accepted.push(file);
      }
      // Replaced rather than appended: these describe THIS pick, and a reason
      // left over from the previous one would read as a second file failing.
      setRefusals([...reasons]);
      if (accepted.length === 0) return;

      const started: PendingFile[] = accepted.map((f) => ({
        localId: `up-${++seq}`,
        name: f.name,
        size: f.size,
      }));
      setPending((prev) => [...prev, ...started]);

      accepted.forEach((file, i) => {
        const row = started[i]!;
        uploadCrmAttachment({ entity, entityId }, file)
          .then((created) => {
            // Appended, because the list is oldest-first: a file that arrived
            // last belongs at the end, which is where the next read will put it
            // too. Prepending would reorder the card under the operator the next
            // time the page is opened.
            setRows((prev) => [...(prev ?? []), created]);
            cb.current.onChange?.();
          })
          .catch((err) => setRefusals((prev) => [...new Set([...prev, refusalKey(err)])]))
          .finally(() => setPending((prev) => prev.filter((p) => p.localId !== row.localId)));
      });
    },
    [entity, entityId, limits],
  );

  const remove = useCallback(async (file: CrmAttachment) => {
    try {
      await deleteCrmAttachment(file.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== file.id));
      cb.current.onChange?.();
    } catch (err) {
      // The row is left where it is. A list that dropped a file the server still
      // holds would be telling the operator a delete succeeded.
      //
      // The error is BOUND rather than swallowed by a bare `catch`, because the
      // server took the trouble to distinguish a storage refusal from a bug and
      // a bare catch threw that distinction away one line before it could be
      // used. What comes out is a key, not the error: see `writeFailureKey`.
      cb.current.onWriteFailed?.(writeFailureKey(err));
    }
  }, []);

  const count = (rows?.length ?? 0) + pending.length;

  return {
    rows,
    limits,
    pending,
    refusals,
    count,
    full: limits !== null && count >= limits.maxFiles,
    uploading: pending.length > 0,
    add,
    remove,
  };
}
